import { createAsyncThunk } from '@reduxjs/toolkit';
import callApi from '../../utils/api/callAPI';

let abortControllerFetchCart = null;
export const fetchCart = createAsyncThunk(
  'get/rooms/v3/design/get_cart',
  async (
    { couponCodes, customerEmail = undefined, countryCode },
    { dispatch }
  ) => {
    if (abortControllerFetchCart) {
      abortControllerFetchCart.abort();
    }
    abortControllerFetchCart = new AbortController();
    try {
      const res = await callApi({
        endpoint: 'rooms/v3/design/get_cart/',
        method: 'get',
        params: {
          coupon_code: couponCodes,
          customer_email: customerEmail,
          country_code: countryCode,
        },
        signal: abortControllerFetchCart.signal,
      });

      const response = res.data;
      if (response.success) {
        const data = response?.result?.data;
        return { data };
      } else {
        throw response;
      }
    } catch (error) {
      dispatch({ type: 'fail', payload: error });
    }
  }
);

export const addToCart = createAsyncThunk(
  'post/rooms/v3/design/{design_id}/add_to_cart',
  async ({ designId, variantId, customerEmail }, { dispatch }) => {
    try {
      const res = await callApi({
        endpoint: 'rooms/v3/design/' + designId + '/add_to_cart',
        method: 'post',
        data: {
          variant_id: variantId,
          customer_email: customerEmail,
        },
      });

      const response = res.data;
      if (response.success) {
        const data = response?.result?.data;
        return { data };
      } else {
        throw response;
      }
    } catch (error) {
      dispatch({ type: 'fail', payload: error });
      return {
        error: error.message,
      };
    }
  }
);

export const removeCart = createAsyncThunk(
  'delete/rooms/v3/design/cart/delete/{cart_id}',
  async ({ cartId }, { dispatch }) => {
    try {
      const res = await callApi({
        endpoint: 'rooms/v3/design/cart/delete/' + cartId,
        method: 'delete',
      });

      const response = res.data;
      if (response.success) {
        return {};
      } else {
        throw response;
      }
    } catch (error) {
      dispatch({ type: 'fail', payload: error });
      return {
        error: error.message,
      };
    }
  }
);

export const applyCoupon = createAsyncThunk(
  'post/rooms/v3/design/cart/apply_coupon/{coupon_code}',
  async ({ couponCode }, { dispatch }) => {
    try {
      const res = await callApi({
        endpoint: 'rooms/v3/design/cart/apply_coupon/' + couponCode + '/',
        method: 'post',
      });

      const response = res.data;
      if (response.success) {
        return { data: response?.result?.data };
      } else {
        throw response;
      }
    } catch (error) {
      dispatch({ type: 'fail', payload: error });
      return {
        error: error.message,
      };
    }
  }
);

export const createOrder = createAsyncThunk(
  'post/rooms/v3/design/order/create',
  async (
    {
      couponCodes,
      items,
      bill_address,
      amount,
      type,
      discount,
      estimated_delivery_date,
      order_type,
    },
    { dispatch }
  ) => {
    try {
      const res = await callApi({
        endpoint: 'rooms/v3/design/order/create/',
        method: 'post',
        data: {
          coupon_codes: JSON.stringify(couponCodes),
          items: JSON.stringify(items),
          bill_address: bill_address,
          amount,
          type,
          discount,
          estimated_delivery_date,
          order_type,
        },
      });

      const response = res.data;
      if (response.success) {
        return { data: response?.result?.data };
      } else {
        throw response;
      }
    } catch (error) {
      dispatch({ type: 'fail', payload: error });
      return {
        error: error.message,
      };
    }
  }
);

export const createPayment = createAsyncThunk(
  'post/rooms/v3/design/order/create_payment',
  async (
    {
      billAddress,
      customerPhone,
      amountPaid,
      paymentTerm,
      paymentMethod,
      orderCode,
      returnUrl,
    },
    { dispatch }
  ) => {
    try {
      const res = await callApi({
        endpoint: 'rooms/v3/design/order/create_payment',
        method: 'post',
        data: {
          bill_address: billAddress,
          payment_method: paymentMethod,
          order_code: orderCode,
          return_url: returnUrl,
          amount_paid: amountPaid,
          payment_term: paymentTerm,
          customer_phone: customerPhone,
        },
      });

      const response = res.data;
      if (response.success) {
        return { data: response?.result?.data };
      } else {
        throw response;
      }
    } catch (error) {
      dispatch({ type: 'fail', payload: error });
      return {
        error: error.message,
      };
    }
  }
);

export const verifyPayment = createAsyncThunk(
  'get/rooms/v3/design/order/verify_payment',
  async ({ params }, { dispatch }) => {
    try {
      const res = await callApi({
        endpoint: 'rooms/v3/design/order/verify_payment' + params,
        method: 'get',
      });

      const response = res.data;
      if (response.success) {
        return { data: response?.result?.data, total: response?.result?.total };
      } else {
        throw response;
      }
    } catch (error) {
      dispatch({ type: 'fail', payload: error });
      return {
        error: error.message,
      };
    }
  }
);

let abortControllerFetchOrders = null;

export const fetchOrders = createAsyncThunk(
  'get/rooms/v3/design/order/list',
  async (
    { offset, limit, fulfillment_status, fulfillment_status_range, kw },
    { dispatch }
  ) => {
    try {
      if (abortControllerFetchOrders) {
        abortControllerFetchOrders.abort();
      }
      abortControllerFetchOrders = new AbortController();
      const res = await callApi({
        endpoint: 'rooms/v3/design/order/list/',
        method: 'get',
        params: {
          offset,
          limit,
          fulfillment_status,
          fulfillment_status_range,
          kw,
        },
        signal: abortControllerFetchOrders.signal,
      });

      const response = res.data;
      if (response.success) {
        return { data: response?.result?.data, total: response?.count ?? 0 };
      } else {
        throw response;
      }
    } catch (error) {
      dispatch({ type: 'fail', payload: error });
      return {
        error: error.message,
      };
    }
  }
);

export const fetchOrder = createAsyncThunk(
  'get/rooms/v3/design/order/{id}/',
  async ({ id, country_code }, { dispatch }) => {
    try {
      const res = await callApi({
        endpoint: 'rooms/v3/design/order/' + id + '/',
        method: 'get',
        params: { country_code },
      });

      const response = res.data;
      if (response.success) {
        return { data: response?.result?.data };
      } else {
        throw response;
      }
    } catch (error) {
      dispatch({ type: 'fail', payload: error });
      return {
        error: error.message,
      };
    }
  }
);

export const updateOrder = createAsyncThunk(
  'get/rooms/v3/design/order/{id}/update/',
  async (
    {
      billAddress,
      amountPaid,
      type,
      phone,
      status,
      actualDeliveryDate,
      id,
      cancelPending,
      fulfillmentStatus,
    },
    { dispatch }
  ) => {
    try {
      const res = await callApi({
        endpoint: 'rooms/v3/design/order/' + id + '/update/',
        method: 'put',
        data: {
          bill_address: billAddress,
          amount_paid: amountPaid,
          type,
          phone,
          status,
          actual_delivery_date: actualDeliveryDate,
          cancel_pending: cancelPending,
          fulfillment_status: fulfillmentStatus,
        },
      });

      const response = res.data;
      if (response.success) {
        return { data: response?.result?.data };
      } else {
        throw response;
      }
    } catch (error) {
      dispatch({ type: 'fail', payload: error });
      return {
        error: error.message,
      };
    }
  }
);
